import FilterTags from '../car-search/2022/filter-tags';

import {
  formatSelectedFiltersData,
} from '../car-search/2023/car-search-helper';

/**
 * Handle SearchWatcherForm's client side logic.
 *
 * SearchWatcherForm.init() is called in master.js to initializes the functionality.
 * Brand and model selection functionality is borrowed from car-search.js and modified slightly to fit this use case.
 */
class SearchWatcherForm {

  /**
   * Last brand to search. This is used when searching brands
   * If brand is different than last search, get new models
   *
   * @type {string[]}
   * @private
   */
  #lastBrandToSearch = [];

  /**
   * Is model selected enabled or disabled.
   * @param {boolean} if true it is enabled false otherwise
   */
  #isModelSelectEnabled = false;

  /**
   * Tax value inputs
   *
   * @type {object}
   * @private
   */
  #taxValueInputs = {
    limited_benefit_min: document.querySelector('input[name="limited_benefit_min"]'),
    limited_benefit_max: document.querySelector('input[name="limited_benefit_max"]'),
    unlimited_benefit_min: document.querySelector('input[name="unlimited_benefit_min"]'),
    unlimited_benefit_max: document.querySelector('input[name="unlimited_benefit_max"]'),
  };

  constructor(element) {
    this._findElements(element);

    this.#lastBrandToSearch =
      window.multiselects['brands[]'].getValue() || false;
    this.handleModelDropdown();

    FilterTags.init();

    window.multiselects['brands[]'].addCallback({
      onValueChange: (multiselectData) => {
        this.handleModelDropdown();
        this._onChange();
      },
    });

    this.dom.form.addEventListener('change', this._onChange.bind(this));

    this.dom.element.querySelectorAll('.predicting-input-field [type="text"]').forEach(element => {
      element.addEventListener('predictingInputChange', this._onChange.bind(this));
    });

    this.dom.form.addEventListener('submit', () => {
      this.dom.submitBtn.disabled = true;
    });

    this.dom.inputs = Array.from(
      document.querySelectorAll('.car-search-effective, select')
    );

    this.checkValidity();
    this.#updateFilterTags(formatSelectedFiltersData(this.dom.inputs));
    this.#handlePropulsionChange();

    this.dom.showFiltersBtn.addEventListener('click', this._onShowFilterBtn.bind(this))
  }

  _findElements(element) {
    this.dom = {};
    this.dom.element = element;
    this.dom.form = element.querySelector('form');
    this.dom.showFiltersBtn = element.querySelector('#show-filters');
    this.dom.searchFilters = element.querySelector('.hedin-search-watcher--form__search-filters');
    this.dom.submitBtn = element.querySelector('button[type="submit"]');
    this.dom.evFilterGroup = element.querySelector('.ev-group');
    this.dom.chademo = element.querySelector('#CHAdeMO').closest('.radio-element');
    this.dom.chipsTitle = element.querySelector('.hedin-search-watch--form__chips-title');
    this.dom.chipHolder = element.querySelector('.hedin-search-watch--form__chips-holder');
  }

  _onChange() {
    this.checkValidity();
    this.#updateFilterTags(formatSelectedFiltersData(this.dom.inputs));
    this.#handlePropulsionChange();
  }

  _onShowFilterBtn() {
    this.dom.showFiltersBtn.classList.add('hidden');
    this.dom.searchFilters.classList.toggle('hedin-search-watcher--form__search-filters--show');
  }

  /**
   * Toggles submit button enabled or disabled depending on is form valid or not.
   * Usefull? not sure.
   */
  checkValidity() {
    if (!this.dom.form.checkValidity()) {
      this.dom.submitBtn.disabled = true;
    } else {
      this.dom.submitBtn.disabled = false;
    }
  }

  set isModelSelectEnabled(state) {
    this.#isModelSelectEnabled = state;
    window.multiselects['models[]'].toggleMultiselect(state);
  }

  /**
   * Updates FilterTags
   *
   * @param {object[]} filters - Filters objects
   * @see FilterTags
   */
  #updateFilterTags(filters) {
    FilterTags.instance.updateFilterTags(filters.filter((f) => !f.noTag));

    if (FilterTags.instance.amount > 0) {
      this.dom.chipHolder.classList.remove('hidden');
      this.dom.chipsTitle.classList.remove('hidden');
    }
  }

  /**
   * Manipulate handle modal facet filters according to selectedParams
   *
   * @param {object[]} selectedParams         - Selected parameters
   * @param {object}   options                - Options
   * @param {boolean}  options.freeTextSearch - Defines if we are manipulating from freeTextSearch
   */
  async manipulateSearchParameters(selectedParams, options = {}) {
    const currentSelectedParams = formatSelectedFiltersData(this.dom.inputs);

    const filtersToRemove = currentSelectedParams.filter(filter =>
      !selectedParams.find(selectedParam => selectedParam.name === filter.name && selectedParam.value === filter.value)
    );

    const filtersToAdd = selectedParams.filter(filter =>
      currentSelectedParams.findIndex(selectedParam => selectedParam.name === filter.name && selectedParam.value === filter.value) === -1
    );

    filtersToRemove.forEach(async filter => {
      const input = document.querySelector(`input[name="${filter.name}"][value="${filter.value}"], input[type="text"][name="${filter.name}"]`);
      if (input && (input.type === 'checkbox' || input.type === 'radio')) {
        input.checked = false;

        // If parent element is checkbox-range-option, remove checked class
        if (input.parentNode.classList.contains('checkbox-range-option')) {
          input.parentNode.classList.remove('checked');
        }
      }

      // Handle text input removation
      if (input && input.type === 'text') {
        input.value = '';
      }

      if (!input && window.multiselects[filter.name]) {
        const optionToToggle = window.multiselects[filter.name].options.find(o => o.value === filter.value);
        window.multiselects[filter.name].unSelect(optionToToggle);
      }

      // If brands does not exists, run handleModelDropdown to empty and disable models dropdown
      if (filter.name === 'brands[]' && filtersToAdd.findIndex(f => f.name === 'brands[]') === -1) {
        await this.handleModelDropdown();
      }
    })

    filtersToAdd.forEach(async filter => {
      const input = document.querySelector(`input[name="${filter.name}"][value="${filter.value}"], input[type="text"][name="${filter.name}"]`);
      if (input && (input.type === 'checkbox' || input.type === 'radio')) {
        input.checked = true;

        // If parent element is checkbox-range-option, add checked class
        if (input.parentNode.classList.contains('checkbox-range-option')) {
          input.parentNode.classList.add('checked');
        }
      }

      // Handle text inputs last state
      if (input && input.type === 'text') {
        input.value = filter.value;
      }

      // All dropdowns except models
      // Models depends on brands, and models are get ouside of this loop
      if (!input && window.multiselects[filter.name] && filter.name !== 'models[]') {
        const optionToToggle = window.multiselects[filter.name].options.find(o => o.value === filter.value);
        window.multiselects[filter.name].selectOption(optionToToggle);
      }
    });

    // If models exists in filtersToAdd, get models from server
    if (filtersToAdd.findIndex(f => f.name === 'models[]') !== -1 && !options.freeTextSearch) {
      await this.handleModelDropdown(false, filtersToAdd.filter(f => f.name === 'models[]'));
    }

    // Run tertiary change
    Object.keys(window.tertiaries).forEach(tertiary => {
      window.tertiaries[tertiary].inputChange(false);
    });


    this.#updateFilterTags(selectedParams);

    // If no filters are selected (clear all is probably clicked),
    // check that also tax value inputs and plug type inputs are hidden
    if (selectedParams.length === 0) {
      this.#handleTaxValueVisibility();
      this.#handlePropulsionChange();
    }
  }

  /**
   * Called on new cars checkbox change.
   * Hides the accordion and unselects all options if the new cars checkbox is unchecked.
   */
  #handleTaxValueVisibility() {
    const taxValuesDiv = this.dom.element.querySelector('#car-search-filters-tax-values');
    if (!taxValuesDiv) return;

    const accordionParent = taxValuesDiv.closest('.hedin-accordion');
    if (!accordionParent) return;

    const newCarsCheckbox = this.dom.element.querySelector('#uudet-autot');
    const isNewCarChecked = newCarsCheckbox && newCarsCheckbox.checked;

    if (!isNewCarChecked) {
      accordionParent.classList.add('hidden');

      // Empty all the fields of benefitInputs
      Object.values(this.#taxValueInputs).forEach(input => {
        if (input) input.value = '';
      });

    } else {
      // Show the accordion parent if new car is checked
      accordionParent.classList.remove('hidden');
    }
  }

  /**
   * Called on propulsion value change.
   */
  #handlePropulsionChange() {
    const isElectricalPropulsionChosen = window.multiselects['fuel[]'].value.some((fuel) => ['S','P'].includes(fuel)) &&
                                         window.multiselects['fuel[]'].value.length === 1;
    this.dom.evFilterGroup.classList.toggle('hidden', !isElectricalPropulsionChosen);

    // These filters are going to be hidden if propulsion is not electrical or plug-in hybrid.
    // So for that reason we need to unselect all values from these filters.
    if (!isElectricalPropulsionChosen) {
      const plugTypeInputs = this.dom.element.querySelectorAll('input[name="plug_type"]')
      plugTypeInputs.forEach(input => input.checked = false);
      window.multiselects.battery_capacity_min.unSelectAll(false);
      window.multiselects.charging_capacity_min.unSelectAll(false);
      window.multiselects.electrical_range_min.unSelectAll(false);
    }

    // Filters are hidden and shown depending on the propulsion type is electrical or plug in hybrid.
    if( isElectricalPropulsionChosen ) {
      const isPlugInHybrid = window.multiselects['fuel[]'].value.includes('P');

      window.multiselects.battery_capacity_min.element.closest('.car-search-filters').classList.toggle('hidden', isPlugInHybrid);
      window.multiselects.charging_capacity_min.element.closest('.car-search-filters').classList.toggle('hidden', isPlugInHybrid);
      window.multiselects.battery_capacity_min.show(!isPlugInHybrid);
      window.multiselects.charging_capacity_min.show(!isPlugInHybrid);
      this.dom.chademo.classList.toggle('hidden', isPlugInHybrid);

      if( isPlugInHybrid ) {
        window.multiselects.battery_capacity_min.unSelectAll(false);
        window.multiselects.charging_capacity_min.unSelectAll(false);
        this.dom.chademo.checked = false;
      }
    }
  }

  /**
   * Handles model dropdown disabled -states and
   * show or hide optgroups depends on what value
   * brands[] dropdown has.
   * @param {boolean} forceModelSearch Force model search, default false
   * @param {boolean} lastStateModels Last state models, default []
   */
  async handleModelDropdown(forceModelSearch = false, lastStateModels = []) {
    this.isModelSelectEnabled = false;

    // If brands[] dropdown value is empty, unselect and disable
    const brandsDropdown = window.multiselects['brands[]'];
    const modelsDropdown = window.multiselects['models[]'];

    if (!brandsDropdown.getValue().length) {
      modelsDropdown.unSelectAll(false);
      //modelsDropdown.disableMultiselect();
      modelsDropdown.removeOptions(
        modelsDropdown.options.map((o) => o.value),
        false
      );
    }

    if (brandsDropdown.getValue().length) {
      const brandsValue = brandsDropdown.getValue();

      if (brandsValue[0] !== this.#lastBrandToSearch[0]) {
        // If lastBrandToSearch was not false, empty all other values from models
        if (this.#lastBrandToSearch) {
          modelsDropdown.unSelectAll(false);
          modelsDropdown.removeOptions(
            modelsDropdown.options.map((o) => o.value),
            false
          );
        }
        // Get brand models
        const models = await this.#getModelsFromServer(brandsValue);

        modelsDropdown.addOptions(
          models.map((model) => ({
            value: model.slug,
            shownValue: model.name,
            selected: lastStateModels.find((m) => m.value === model.slug),
          }))
        );

        // Update changes values
        this.#lastBrandToSearch = window.multiselects['brands[]'].getValue();
      } else {
        if (lastStateModels.length) {
          lastStateModels.forEach((model) =>
            modelsDropdown.selectOption(model)
          );
        }
      }

      this.isModelSelectEnabled = true;
    }
  }

  /**
   * Get brand models from server
   *
   * @param {string[]} brandSlugs - Brand slugs
   * @returns {object[]}
   */
  async #getModelsFromServer(brandSlugs) {
    const urlSearchParams = new URLSearchParams();
    brandSlugs.forEach((slug) => urlSearchParams.append('brandSlug[]', slug));

    const response = await fetch(
      `/stage-ajax/getModelsByBrand/?${urlSearchParams.toString()}`
    );
    return await response.json();
  }

  static init() {
    document
      .querySelectorAll('.hedin-search-watcher--form')
      .forEach((element) => {
        new SearchWatcherForm(element);
      });
  }
}

export default SearchWatcherForm;
